import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Tag,
  Popconfirm,
  Spin,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import moment from "moment";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";

import {
  generatePairingCode,
  getBranchDevices,
  getBranches,
  registerPOSTerminal,
  unbindDevice,
  getDeviceStatus,
  refreshDeviceToken,
} from "../../../network/network";

import { generateFingerprint } from "../../../utils/device/fingerprint";
import PairingCodeModal from "./components/PairingCodeModal";

import {
  saveDeviceAuth,
  getDeviceAuth,
  clearDeviceAuth,
} from "../../../utils/device/deviceAuth";

import { AppContext } from "../../../context/AppContextProvider";
import {
  DeviceType,
  UserType,
} from "../../../configs/constants";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const getDeviceUidValue = (device) => device?.uid || device?.device_uid;

const isSameDeviceUid = (uid1, uid2) =>
  uid1 && uid2 && String(uid1) === String(uid2);

const CustomerDisplayScreen = () => {
  const { authUser } = useContext(AppContext);
  const { t } = useTranslation();

  const [posDevicesData, setPosDevicesData] = useState([]);
  const [cdsDevicesData, setCdsDevicesData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);

  const [apiResponse, setApiResponse] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const [cdsModalOpen, setCdsModalOpen] = useState(false);
  const [posModalOpen, setPosModalOpen] = useState(false);

  const [dataLoading, setDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);

  const [fingerprint, setFingerprint] = useState("");
  const [posDeviceUid, setPosDeviceUid] = useState("");

  const [form] = Form.useForm();
  const [posForm] = Form.useForm();

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "customer-display-screen",
      breadcrumbName: t("customer_display_screen"),
      icon: <DesktopOutlined />,
    },
  ];

  const resetLocalPOS = useCallback(() => {
    clearDeviceAuth(DeviceType.Pos);
    setPosDeviceUid("");
    setApiResponse(null);
    setRemainingSeconds(0);
    setCdsModalOpen(false);
  }, []);

  // =========================================================
  // Validate local POS auth against server
  // =========================================================
  const ensurePosAuthIsValid = useCallback(async () => {
    const posAuth = getDeviceAuth(DeviceType.Pos);

    if (!posAuth?.token || !posAuth?.device_uid) {
      setPosDeviceUid("");
      return false;
    }

    setPosDeviceUid(posAuth.device_uid);

    try {
      const statusRes = await getDeviceStatus(DeviceType.Pos);

      const ok =
        statusRes?.data?.success === true &&
        statusRes?.data?.data?.is_active !== false;

      if (ok) return true;

      throw new Error("POS inactive/unbound");
    } catch {
      try {
        const fp = fingerprint || (await generateFingerprint());
        if (!fingerprint) setFingerprint(fp);

        const refreshRes = await refreshDeviceToken(
          { device_fingerprint: fp },
          DeviceType.Pos,
        );

        const refreshOk = refreshRes?.data?.success === true;
        const auth = refreshRes?.data?.data;

        if (!refreshOk || !auth?.token) {
          handleApiError(refreshRes, t);
          resetLocalPOS();
          return false;
        }

        const nextUid = auth?.device_uid || auth?.uid || posAuth?.device_uid;

        saveDeviceAuth({
          ...posAuth,
          ...auth,
          device_uid: nextUid,
          device_type: DeviceType.Pos,
        });

        setPosDeviceUid(nextUid || "");
        return true;
      } catch (error) {
        handleApiError(error, t);
        resetLocalPOS();
        return false;
      }
    }
  }, [fingerprint, resetLocalPOS, t]);

  useEffect(() => {
    ensurePosAuthIsValid();
  }, [ensurePosAuthIsValid]);

  // Fingerprint
  useEffect(() => {
    let isEffectActive = true;

    (async () => {
      try {
        const fp = await generateFingerprint();
        if (!isEffectActive) return;
        setFingerprint(fp);

        const posAuth = getDeviceAuth(DeviceType.Pos);
        setPosDeviceUid(posAuth?.device_uid || "");
      } catch {
        if (!isEffectActive) return;

        setFingerprint("");
        const posAuth = getDeviceAuth(DeviceType.Pos);
        setPosDeviceUid(posAuth?.device_uid || "");
      }
    })();

    return () => {
      isEffectActive = false;
    };
  }, []);

  // branches
  useEffect(() => {
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setBranchesData([]);
          return;
        }

        setBranchesData(response?.data?.data?.branches || []);
      })
      .catch((error) => {
        handleApiError(error, t);
        setBranchesData([]);
      });
  }, [t]);

  // Load devices
  useEffect(() => {
    setDataLoading(true);

    getBranchDevices()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setPosDevicesData([]);
          setCdsDevicesData([]);
          return;
        }

        const devices = response?.data?.data?.devices || [];

        setPosDevicesData(
          devices.filter((d) => d.device_type === DeviceType.Pos),
        );
        setCdsDevicesData(
          devices.filter((d) => d.device_type === DeviceType.Cds),
        );
      })
      .catch((error) => {
        handleApiError(error, t);
        setPosDevicesData([]);
        setCdsDevicesData([]);
      })
      .finally(() => setDataLoading(false));
  }, [reloadTableData, t]);

  // Pairing code countdown
  useEffect(() => {
    if (!apiResponse?.expires_at) return;

    const firstDiff = moment(apiResponse.expires_at).diff(moment(), "seconds");
    setRemainingSeconds(firstDiff > 0 ? firstDiff : 0);

    const interval = setInterval(() => {
      const diff = moment(apiResponse.expires_at).diff(moment(), "seconds");
      setRemainingSeconds(diff > 0 ? diff : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [apiResponse]);

  // -----------------------------
  // POS Register
  // -----------------------------
  const openRegisterPOSModal = () => {
    const posAuth = getDeviceAuth(DeviceType.Pos);

    if (posAuth?.device_uid) {
      NotificationWithIcon(
        "warning",
        t("something_went_wrong"),
        t("pos_terminal_already_registered"),
      );
      return;
    }

    setPosModalOpen(true);
    posForm.resetFields();
  };

  const closeRegisterPOSModal = () => {
    posForm.resetFields();
    setPosModalOpen(false);
  };

  const handleRegisterPOS = async () => {
    try {
      const values = await posForm.validateFields();
      setDataLoading(true);

      const fp = fingerprint || (await generateFingerprint());

      const posAuth = getDeviceAuth(DeviceType.Pos);
      if (posAuth?.device_uid) {
        NotificationWithIcon(
          "warning",
          t("something_went_wrong"),
          t("pos_terminal_already_registered"),
        );
        setDataLoading(false);
        return;
      }

      const payload = {
        device_fingerprint: fp,
        device_name: values.device_name?.trim() || undefined,
      };

      const res = await registerPOSTerminal(payload);
      const data = res?.data || res?.response?.data;

      if (data?.success === true) {
        const d = data?.data || {};
        const uid = d?.device_uid || d?.uid;

        if (uid) {
          saveDeviceAuth({
            ...d,
            device_uid: uid,
            device_type: DeviceType.Pos,
          });

          setFingerprint(fp);
          setPosDeviceUid(uid);
        }

        setReloadTableData((p) => !p);
        NotificationWithIcon(
          "success",
          t("device_added"),
          t("device_added_successfully"),
        );

        closeRegisterPOSModal();
      } else {
        handleApiError(res, t);
      }
    } catch (err) {
      if (err?.errorFields?.length) return;
      handleApiError(err, t);
    } finally {
      setDataLoading(false);
    }
  };

  // -----------------------------
  // CDS Pairing Code
  // -----------------------------
  const openCreateCDSModal = () => {
    if (!posDeviceUid) {
      showAppError(t("register_pos_terminal_first"));
      return;
    }

    setCdsModalOpen(true);
    form.resetFields();
    setApiResponse(null);
    setRemainingSeconds(0);
  };

  const closeCreateCDSModal = () => {
    setCdsModalOpen(false);
    form.resetFields();
    setApiResponse(null);
    setRemainingSeconds(0);
    setReloadTableData((p) => !p);
  };

  const handleCreateCDS = async () => {
    if (dataLoading) return;

    try {
      const values = await form.validateFields();

      if (!posDeviceUid) {
        showAppError(t("register_pos_terminal_first"));
        return;
      }

      setDataLoading(true);

      const payload = {
        device_type: DeviceType.Cds,
        device_name: values.device_name?.trim() || undefined,
        branch_id: values.branch_id,
        linked_pos_device_uid: posDeviceUid,
      };

      const response = await generatePairingCode(payload);
      const data = response?.data || response?.response?.data;

      if (data?.success === true) {
        setApiResponse(data?.data);
      } else {
        handleApiError(response, t);
      }
    } catch (err) {
      if (err?.errorFields?.length) return;
      handleApiError(err, t);
    } finally {
      setDataLoading(false);
    }
  };

  // -----------------------------

  const isOwner = authUser?.type === UserType.BusinessOwner;

  const canUnbind = useCallback(
    (device) => {
      if (isOwner) return true;

      if (!posDeviceUid) return false;

      if (device?.device_type === DeviceType.Pos) {
        return isSameDeviceUid(getDeviceUidValue(device), posDeviceUid);
      }

      if (device?.device_type === DeviceType.Cds) {
        return isSameDeviceUid(device?.linked_pos_device_uid, posDeviceUid);
      }

      return false;
    },
    [posDeviceUid, isOwner],
  );

  const visiblePosDevices = useMemo(() => {
    if (isOwner) return posDevicesData || [];
    return (posDevicesData || []).filter((d) => canUnbind(d));
  }, [isOwner, posDevicesData, canUnbind]);

  const visibleCdsDevices = useMemo(() => {
    if (isOwner) return cdsDevicesData || [];
    return (cdsDevicesData || []).filter((d) => canUnbind(d));
  }, [isOwner, cdsDevicesData, canUnbind]);

  // unbind cds when unbinding POS
  const unbindLinkedCdsForPos = useCallback(
    async (posUid) => {
      const linked = (cdsDevicesData || []).filter(
        (d) => d?.linked_pos_device_uid === posUid,
      );

      if (!linked.length) return { total: 0, ok: 0, failed: 0 };

      const results = await Promise.allSettled(
        linked.map((d) => unbindDevice(d.uid)),
      );

      let ok = 0;
      let failed = 0;

      results.forEach((r) => {
        const success =
          r.status === "fulfilled" && r.value?.data?.success === true;
        if (success) ok += 1;
        else failed += 1;
      });

      const firstFailure = results.find(
        (result) =>
          result.status === "rejected" || result.value?.data?.success === false,
      );

      if (firstFailure) {
        handleApiError(
          firstFailure.status === "rejected"
            ? firstFailure.reason
            : firstFailure.value,
          t,
        );
      }

      return { total: linked.length, ok, failed };
    },
    [cdsDevicesData, t],
  );

  const handleUnbind = async (device) => {
    const uid = device?.uid;
    if (!uid) return;

    if (!canUnbind(device)) {
      showAppError(t("something_went_wrong"));
      return;
    }

    try {
      setDataLoading(true);

      // ✅ If POS is being unbound -> unbind all linked CDS first
      if (device?.device_type === DeviceType.Pos) {
        await unbindLinkedCdsForPos(uid);
      }

      const response = await unbindDevice(uid);

      if (response?.data?.success === true) {
        if (uid === posDeviceUid) {
          resetLocalPOS();
        }

        setReloadTableData((p) => !p);
        NotificationWithIcon(
          "success",
          t("device_unbind"),
          t("device_unbind_successfully"),
        );
      } else {
        handleApiError(response, t);
      }
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setDataLoading(false);
    }
  };

  const hasMyCds = useMemo(() => {
    if (!posDeviceUid) return false;
    return (cdsDevicesData || []).some((d) =>
      isSameDeviceUid(d?.linked_pos_device_uid, posDeviceUid),
    );
  }, [cdsDevicesData, posDeviceUid]);

  const renderTypeTag = (type) => {
    if (type === DeviceType.Pos)
      return (
        <Tag
          style={{
            color: "var(--color-primary-hover)",
            borderColor: "var(--color-primary)",
            background: "var(--color-primary-soft)",
          }}
        >
          {t("pos_terminal")}
        </Tag>
      );
    if (type === DeviceType.Cds)
      return <Tag color="geekblue">{t("customer_display_screen")}</Tag>;
    return <Tag>{type || "-"}</Tag>;
  };

  const columns = [
    {
      title: "#",
      className: "app-table-index-column",
      render: (t2, r, index) => <>{index + 1}</>,
    },
    {
      title: t("type"),
      dataIndex: "device_type",
      render: (v) => renderTypeTag(v),
    },
    {
      title: t("name"),
      dataIndex: "device_name",
      render: (v) => v || "-",
    },
    {
      title: t("bound_by"),
      dataIndex: "bound_by",
      render: (v) => v || "-",
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return branch ? branch.name : "-";
      },
    },
    {
      title: t("last_seen"),
      dataIndex: "last_seen_at",
      render: (v) => (v ? moment(v).format("MMM D, YYYY hh:mm a") : "-"),
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (v) => (v ? moment(v).format("MMM D, YYYY hh:mm a") : "-"),
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (_, record) =>
        canUnbind(record) ? (
          <Popconfirm
            title={
              record?.device_type === DeviceType.Pos
                ? t("unbind_pos_and_cds")
                : t("unbind_this_device")
            }
            okText={t("yes")}
            cancelText={t("no")}
            placement="topLeft"
            onConfirm={() => handleUnbind(record)}
          >
            <Button type="link" title={t("delete_entity")}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        ) : (
          <span style={{ opacity: 0.35 }}>—</span>
        ),
    },
  ];

  const showRegisterPosBtn = !posDeviceUid;
  const showAddCdsBtn = !!posDeviceUid && !hasMyCds;

  return (
    <>
      <Spin spinning={dataLoading}>
        <Card className="app-mb-20">
          <CustomBreadcrumb menus={breadcrumbRoutes} />
          <PageHeader
            className="app-p-0"
            title={t("customer_display_screen")}
            ghost={false}
            extra={[
              showRegisterPosBtn ? (
                <Button
                  key="register-pos-terminal"
                  type="primary"
                  onClick={openRegisterPOSModal}
                >
                  {t("register_pos_terminal")}
                </Button>
              ) : showAddCdsBtn ? (
                <Button
                  key="add-cds"
                  type="primary"
                  onClick={openCreateCDSModal}
                >
                  {t("add_customer_screen")}
                </Button>
              ) : null,
            ].filter(Boolean)}
          />
        </Card>

        <Card className="app-mb-16" title={t("pos_terminal")}>
          <Table
            columns={columns}
            dataSource={visiblePosDevices}
            rowKey={(record) => getDeviceUidValue(record)}
            pagination={false}
          />
        </Card>

        <Card title={t("customer_display_screen")}>
          <Table
            columns={columns}
            dataSource={visibleCdsDevices}
            rowKey={(record) => getDeviceUidValue(record)}
            pagination={false}
          />
        </Card>

        <PairingCodeModal
          open={cdsModalOpen}
          title={t("add_customer_screen")}
          onCancel={closeCreateCDSModal}
          form={form}
          branchesData={branchesData}
          dataLoading={dataLoading}
          apiResponse={apiResponse}
          remainingSeconds={remainingSeconds}
          onGenerate={handleCreateCDS}
          onResetPairing={() => {
            setApiResponse(null);
            setRemainingSeconds(0);
          }}
          showLinkedPosUid={true}
          posDeviceUid={posDeviceUid}
          notRegisteredText={t("not_registered")}
          deviceType={DeviceType.Cds}
        />

        <Modal
          open={posModalOpen}
          title={t("register_pos_terminal")}
          onOk={handleRegisterPOS}
          onCancel={closeRegisterPOSModal}
          confirmLoading={dataLoading}
          okText={t("register_pos_terminal")}
          maskClosable={false}
        >
          <Form layout="vertical" form={posForm}>
            <Form.Item
              name="device_name"
              label={t("name")}
              rules={[
                {
                  required: true,
                  message: t("validate_name_required"),
                  whitespace: true,
                },
              ]}
              hasFeedback
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </>
  );
};

export default CustomerDisplayScreen;
